import { TheraAdvanceHistoryEntry } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/advance/resources/theraAdvance';
import {
  WorkerTransaction,
  WorkerWithdrawalTransaction,
  WorkerInvoicePaymentTransaction,
  MiscellaneousWorkerCreditTransaction,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bank';
import { WalletHistory } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import { DateTime } from 'luxon';

import {
  TheraAdvanceTransactionType,
  getTheraAdvanceTransactionTextStyle,
  workerTransactionTextStyle,
  withdrawalTransactionTextStyle,
  HIDDEN_LEDGER_TRANSACTION_IDS,
} from 'lib/constants';
import { IPillStatus } from 'components/app-components/ui/status-pill';

export type TransactionTypeFilter = 'thera_advance' | 'withdrawals' | 'wallet' | 'all';

export type UnifiedTransactionType =
  | 'thera_advance_withdrawal'
  | 'thera_advance_repayment'
  | 'withdrawals'
  | 'invoice_payment'
  | 'miscellaneous'
  | 'wallet_debit'
  | 'wallet_credit';

export interface UnifiedTransaction {
  id: string;
  type: UnifiedTransactionType;
  date: Date;
  status: string;
  pillStatus: IPillStatus;
  amount: number; // in cents, always positive — use isCredit for direction
  currency: string;
  isCredit: boolean;
  withdrawalMethodAccountType?: string;
  ledgerTransactionId?: string;
  withdrawalMethodId?: string;
  companyName?: string;
  convertedAmount?: number;
  convertedCurrency?: string;
}

export const UNIFIED_TYPE_LABELS: Record<UnifiedTransactionType, string> = {
  thera_advance_withdrawal: 'Thera Advance Withdrawal',
  thera_advance_repayment: 'Thera Advance Repayment',
  withdrawals: 'Withdrawal',
  invoice_payment: 'Invoice Payment',
  miscellaneous: 'Miscellaneous',
  wallet_debit: 'Wallet Debit',
  wallet_credit: 'Wallet Credit',
};

export const TRANSACTION_TYPE_LABELS: Record<TransactionTypeFilter, string> = {
  thera_advance: 'Thera Advance',
  withdrawals: 'Withdrawals',
  wallet: 'Wallet',
  all: 'All Transactions',
};

export function formatTransactionDateTime(date: Date): { date: string; time: string } {
  const dt = DateTime.fromJSDate(date, { zone: 'local' });
  return {
    date: dt.toFormat('d LLL, yyyy'),
    time: dt.toFormat('hh:mm a'),
  };
}

export function normalizeTheraAdvanceEntries(entries: TheraAdvanceHistoryEntry[]): UnifiedTransaction[] {
  return (entries || []).map((e) => {
    const isWithdrawal = e.type === TheraAdvanceTransactionType.WITHDRAWAL;
    const advanceStyle = getTheraAdvanceTransactionTextStyle(e.status, isWithdrawal);
    return {
      id: e.id,
      type: isWithdrawal ? 'thera_advance_withdrawal' : 'thera_advance_repayment',
      date: e.date ? DateTime.fromISO(e.date, { zone: 'local' }).toJSDate() : DateTime.now().toJSDate(),
      status: advanceStyle?.text ?? e.status,
      pillStatus: (advanceStyle?.status ?? 'inProgress') as IPillStatus,
      amount: Math.abs(e.amount),
      currency: 'USD',
      isCredit: false,
      ledgerTransactionId: e.ledgerTransactionId,
      withdrawalMethodId: isWithdrawal ? e.withdrawalMethodId : undefined,
      convertedAmount: e.convertedAmount ?? undefined,
      convertedCurrency: e.convertedCurrency ?? undefined,
    };
  });
}

export function normalizeWorkerTransactions(transactions: WorkerTransaction[]): UnifiedTransaction[] {
  return (transactions || [])
    .filter((txn) => {
      if (txn.details.type !== 'withdrawal') return true;
      return !HIDDEN_LEDGER_TRANSACTION_IDS.includes((txn.details as WorkerWithdrawalTransaction).ledgerTransactionId);
    })
    .map((txn) => {
      const { details, amount, currency, convertedAmount, convertedCurrency, timestamp, status } = txn;
      const finalCurrency = convertedCurrency ?? currency;

      if (details.type === 'invoicePayment') {
        const invoiceDetails = details as WorkerInvoicePaymentTransaction;
        const invoicePaymentStatus =
          status === 'PENDING' ? 'Open' : (workerTransactionTextStyle[status]?.text ?? String(status));
        const invoicePaymentPillStatus =
          status === 'PENDING' ? 'neutral' : (workerTransactionTextStyle[status]?.status ?? 'inProgress');
        return {
          id: `${timestamp?.getTime()}-${amount}-${invoiceDetails.companyName ?? ''}`,
          type: 'invoice_payment',
          date: timestamp ? DateTime.fromJSDate(timestamp, { zone: 'local' }).toJSDate() : DateTime.now().toJSDate(),
          status: invoicePaymentStatus,
          pillStatus: invoicePaymentPillStatus as IPillStatus,
          amount: Math.abs(convertedAmount ?? amount),
          currency: finalCurrency,
          isCredit: true,
          companyName: invoiceDetails.companyName,
        };
      }

      if (details.type === 'miscellaneous') {
        const miscDetails = details as MiscellaneousWorkerCreditTransaction;
        return {
          id: miscDetails.ledgerTransactionId ?? `misc-${String(timestamp)}`,
          type: 'miscellaneous',
          date: timestamp ? DateTime.fromJSDate(timestamp, { zone: 'local' }).toJSDate() : DateTime.now().toJSDate(),
          status: workerTransactionTextStyle[status]?.text ?? String(status),
          pillStatus: (workerTransactionTextStyle[status]?.status ?? 'inProgress') as IPillStatus,
          amount: Math.abs(convertedAmount ?? amount),
          currency: finalCurrency,
          isCredit: true,
        };
      }

      const withdrawalDetails = details as WorkerWithdrawalTransaction;
      return {
        id: withdrawalDetails.ledgerTransactionId,
        type: 'withdrawals',
        date: timestamp ? DateTime.fromJSDate(timestamp, { zone: 'local' }).toJSDate() : DateTime.now().toJSDate(),
        status: withdrawalTransactionTextStyle[status]?.text ?? String(status),
        pillStatus: (withdrawalTransactionTextStyle[status]?.status ?? 'inProgress') as IPillStatus,
        amount: Math.abs(amount),
        currency,
        isCredit: false,
        withdrawalMethodAccountType: withdrawalDetails.withdrawalMethodAccountType,
        ledgerTransactionId: withdrawalDetails.ledgerTransactionId,
        withdrawalMethodId: withdrawalDetails.withdrawalMethodId,
        convertedAmount: convertedAmount ?? undefined,
        convertedCurrency: convertedCurrency ?? undefined,
      };
    });
}

export function normalizeWalletTransactions(walletTransactions: WalletHistory[]): UnifiedTransaction[] {
  return (walletTransactions || []).map((tx) => {
    const isDebit = tx.source?.paymentRail === 'bridge_wallet';
    const currency = tx.source?.currency?.toUpperCase() ?? 'USD';
    const amountCents = Math.round(Number(tx.amount) * 100);
    return {
      id: `${tx.customerId}-${tx.createdAt}`,
      type: isDebit ? 'wallet_debit' : 'wallet_credit',
      date: tx.createdAt ? DateTime.fromISO(tx.createdAt, { zone: 'local' }).toJSDate() : DateTime.now().toJSDate(),
      status: 'Completed',
      pillStatus: 'ready',
      amount: amountCents,
      currency,
      isCredit: !isDebit,
    };
  });
}

export function mergeAndSortTransactions(...groups: UnifiedTransaction[][]): UnifiedTransaction[] {
  return groups.flat().sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function filterUnifiedByType(
  transactions: UnifiedTransaction[],
  type: TransactionTypeFilter,
): UnifiedTransaction[] {
  if (type === 'all') return transactions;
  const filterMap: Record<TransactionTypeFilter, (txn: UnifiedTransaction) => boolean> = {
    thera_advance: (txn) => txn.type === 'thera_advance_withdrawal' || txn.type === 'thera_advance_repayment',
    withdrawals: (txn) => txn.type === 'withdrawals',
    wallet: (txn) => txn.type === 'wallet_debit' || txn.type === 'wallet_credit',
    all: () => true,
  };
  return transactions.filter(filterMap[type]);
}
